import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash, GasPump, Drop, Trophy, Tag, Star, PencilSimple } from "@phosphor-icons/react";
import { parseNum, parseInt10, fmtDate } from "@/lib/utils";
import { BR_STATES } from "@/lib/br";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { PasswordConfirmDialog } from "@/components/PasswordConfirmDialog";
import { AuditLogCard } from "@/components/AuditLogCard";

const EMPTY = { trip_id: "", fuel_type: "diesel", date: "", city: "", state: "", liters: "", unit_price: "", total: 0, km_at_fueling: "", payment_method: "carta_frete", paid_by: "empresa", station_name: "", station_id: "" };
const EMPTY_PRICE = { station_name: "", fuel_type: "diesel", price: "", city: "", state: "", date: "", notes: "" };
const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtL = (n) => (Number(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 3 });

export default function Fueling() {
  const [items, setItems] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stations, setStations] = useState([]);
  const [cheapest, setCheapest] = useState({ diesel: null, arla: null });
  const [open, setOpen] = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceForm, setPriceForm] = useState(EMPTY_PRICE);
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState("diesel");
  const [editingId, setEditingId] = useState(null);
  const [pwAction, setPwAction] = useState(null); // { kind: "edit"|"delete", id, payload }

  const load = async () => {
    const [f, t, s, cd, ca] = await Promise.all([
      api.get("/fueling"),
      api.get("/trips"),
      api.get("/suppliers?type=posto").catch(() => ({ data: [] })),
      api.get("/fuel-prices/cheapest?fuel_type=diesel").catch(() => ({ data: null })),
      api.get("/fuel-prices/cheapest?fuel_type=arla").catch(() => ({ data: null })),
    ]);
    setItems(f.data); setTrips(t.data); setStations(s.data || []);
    setCheapest({ diesel: cd.data, arla: ca.data });
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (e.detail?.entity === "fueling") load(); };
    window.addEventListener("live-event", h);
    return () => window.removeEventListener("live-event", h);
  }, []);

  const set = (k, isNum = false) => (e) => {
    const raw = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: raw }));
  };

  // Auto-calc total
  useEffect(() => {
    const total = parseNum(form.liters) * parseNum(form.unit_price);
    setForm((f) => ({ ...f, total }));
    // eslint-disable-next-line
  }, [form.liters, form.unit_price]);

  const FUEL_NUM_FIELDS = ["liters", "unit_price"];
  const FUEL_KM_FIELDS = ["km_at_fueling"];

  const startNew = (ft) => { setForm({ ...EMPTY, fuel_type: ft }); setEditingId(null); setOpen(true); };
  const startEdit = (item) => { setForm({ ...EMPTY, ...item }); setEditingId(item.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    FUEL_NUM_FIELDS.forEach((k) => { payload[k] = parseNum(payload[k]); });
    FUEL_KM_FIELDS.forEach((k) => { payload[k] = parseInt10(payload[k]); });
    payload.total = parseNum(payload.total);
    if (editingId) {
      // Editar exige senha
      setPwAction({ kind: "edit", id: editingId, payload });
    } else {
      try {
        await api.post("/fueling", payload);
        toast.success("Abastecimento registrado");
        setOpen(false); load();
      } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    }
  };

  const askDelete = (id) => setPwAction({ kind: "delete", id });

  const runPwAction = async () => {
    if (!pwAction) return;
    try {
      if (pwAction.kind === "delete") {
        await api.delete(`/fueling/${pwAction.id}`);
        toast.success("Abastecimento removido");
      } else if (pwAction.kind === "edit") {
        await api.put(`/fueling/${pwAction.id}`, pwAction.payload);
        toast.success("Abastecimento atualizado");
        setOpen(false);
      }
      setPwAction(null);
      window.dispatchEvent(new CustomEvent("audit-log-refresh", { detail: { entity: "fueling" } }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  const submitPrice = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...priceForm, price: parseNum(priceForm.price) };
      if (!payload.station_name || !payload.price) return toast.error("Informe posto e preço");
      await api.post("/fuel-prices", payload);
      toast.success("Preço registrado!");
      setPriceForm(EMPTY_PRICE);
      setPriceOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar preço");
    }
  };

  const filtered = items.filter((i) => i.fuel_type === tab);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
          <GasPump size={32} weight="duotone" className="text-blue-600" /> Abastecimentos
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Diesel e ARLA registrados separadamente</p>
      </div>

      {/* Postos mais baratos + Registrar preço */}
      {cheapest[tab] && (cheapest[tab].ranking?.length > 0 || cheapest[tab].latest) && (
        <Card data-testid="cheapest-card" className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-white">
          <CardHeader className="flex-row items-center justify-between pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
              <Trophy size={20} weight="fill" className="text-yellow-500" />
              Top {tab === "diesel" ? "Diesel" : "ARLA"} mais barato
            </CardTitle>
            <Button size="sm" onClick={() => { setPriceForm({ ...EMPTY_PRICE, fuel_type: tab }); setPriceOpen(true); }} data-testid="btn-register-price" className="bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-1 h-8">
              <Tag size={14} weight="fill" /> Registrar preço
            </Button>
          </CardHeader>
          <CardContent>
            {cheapest[tab].latest && (
              <div className="mb-3 p-2 rounded-md bg-yellow-100 border border-yellow-300 flex items-center gap-2" data-testid="latest-price-highlight">
                <Star size={18} weight="fill" className="text-yellow-600 shrink-0" />
                <div className="text-sm flex-1">
                  <div className="text-xs uppercase tracking-wider text-yellow-800 font-bold">Última inserção</div>
                  <div className="font-semibold text-slate-800">
                    {cheapest[tab].latest.station_name}
                    {cheapest[tab].latest.city && <span className="text-slate-500 font-normal"> · {cheapest[tab].latest.city}/{cheapest[tab].latest.state}</span>}
                  </div>
                </div>
                <div className="text-xl font-extrabold text-yellow-900">R$ {fmtL(cheapest[tab].latest.price)}<span className="text-xs font-normal text-slate-600">/L</span></div>
              </div>
            )}
            <div className="space-y-1.5" data-testid="cheapest-ranking">
              {cheapest[tab].ranking.map((r, i) => (
                <div key={r.station_id || r.station_name} className={`flex items-center gap-2 p-2 rounded ${i === 0 ? "bg-emerald-100 border border-emerald-300" : "bg-white border border-slate-100"}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? "bg-emerald-600 text-white" : i === 1 ? "bg-slate-400 text-white" : i === 2 ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{r.station_name}</div>
                    <div className="text-xs text-slate-500">{r.city ? `${r.city}/${r.state}` : ""}{r.date && ` · ${fmtDate(r.date)}`}{r.source === "manual" && " · anotação"}</div>
                  </div>
                  <div className={`text-lg font-extrabold ${i === 0 ? "text-emerald-800" : "text-slate-700"}`}>R$ {fmtL(r.price)}<span className="text-xs font-normal text-slate-500">/L</span></div>
                </div>
              ))}
              {cheapest[tab].ranking.length === 0 && (
                <p className="text-sm text-slate-500 italic">Nenhum preço registrado ainda. Registre abastecimentos ou clique em "Registrar preço" pra montar seu ranking.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto p-1">
          <TabsTrigger value="diesel" data-testid="fuel-tab-diesel" className="py-2 px-4 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white"><GasPump size={16} /> Diesel</TabsTrigger>
          <TabsTrigger value="arla" data-testid="fuel-tab-arla" className="py-2 px-4 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white"><Drop size={16} /> ARLA</TabsTrigger>
        </TabsList>

        {["diesel", "arla"].map((ft) => (
          <TabsContent key={ft} value={ft}>
            <div className="flex justify-end mb-3">
              <Button onClick={() => startNew(ft)} data-testid={`btn-new-${ft}`} className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5"><Plus size={16} /> Novo {ft === "diesel" ? "Diesel" : "ARLA"}</Button>
            </div>
            <Card>
              <CardHeader><CardTitle className="text-lg">{ft === "diesel" ? "Diesel" : "ARLA"} ({items.filter(i => i.fuel_type === ft).length})</CardTitle></CardHeader>
              <CardContent>
                {items.filter(i => i.fuel_type === ft).length === 0 ? <p className="text-muted-foreground text-sm">Nenhum registro.</p> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Posto</TableHead><TableHead>Local</TableHead><TableHead className="text-right">Litros</TableHead><TableHead className="text-right">R$/L</TableHead><TableHead className="text-right">Total</TableHead>{ft === "diesel" && <><TableHead className="text-right">KM painel</TableHead><TableHead className="text-right">KM rodados</TableHead><TableHead className="text-right">Média km/L</TableHead></>}<TableHead>Pagto</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {items.filter(i => i.fuel_type === ft).map((i) => (
                        <TableRow key={i.id}>
                          <TableCell className="text-xs">{fmtDate(i.date)}</TableCell>
                          <TableCell className="text-xs font-medium text-slate-800">{i.station_name || <span className="text-slate-400 italic">—</span>}</TableCell>
                          <TableCell>{i.city}/{i.state}</TableCell>
                          <TableCell className="text-right">{i.liters}</TableCell>
                          <TableCell className="text-right">{fmt(i.unit_price)}</TableCell>
                          <TableCell className="text-right font-semibold">{fmt(i.total)}</TableCell>
                          {ft === "diesel" && <>
                            <TableCell className="text-right text-xs font-mono">{i.km_at_fueling ? i.km_at_fueling.toLocaleString("pt-BR") : "-"}</TableCell>
                            <TableCell className="text-right text-xs">{i.km_since_last ? <span className="text-blue-700 font-semibold">{i.km_since_last.toLocaleString("pt-BR")}</span> : <span className="text-slate-400">-</span>}</TableCell>
                            <TableCell className="text-right text-xs">{i.avg_km_per_liter ? <span className={`font-bold ${i.avg_km_per_liter >= 3 ? "text-emerald-700" : i.avg_km_per_liter >= 2 ? "text-amber-700" : "text-red-700"}`}>{i.avg_km_per_liter.toFixed(2)} km/L</span> : <span className="text-slate-400">-</span>}</TableCell>
                          </>}
                          <TableCell className="text-xs">{i.payment_method}</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => startEdit(i)} data-testid={`btn-edit-fuel-${i.id}`} title="Editar (requer senha)"><PencilSimple size={16} className="text-blue-600" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => askDelete(i.id)} data-testid={`btn-del-fuel-${i.id}`} title="Excluir (requer senha)"><Trash size={16} className="text-red-600" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar" : "Novo"} abastecimento — {form.fuel_type === "diesel" ? "Diesel" : "ARLA"}{editingId ? " (requer senha)" : ""}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Viagem</Label>
              <Select value={form.trip_id} onValueChange={set("trip_id")}>
                <SelectTrigger data-testid="fuel-trip"><SelectValue placeholder="Selecione a viagem" /></SelectTrigger>
                <SelectContent>{trips.map(t => <SelectItem key={t.id} value={t.id}>{t.origin} → {t.destination} ({fmtDate(t.date_departure)})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Posto de abastecimento</Label>
              <Input
                list="stations-datalist"
                value={form.station_name}
                onChange={(e) => setForm((f) => ({ ...f, station_name: e.target.value, station_id: "" }))}
                placeholder="Ex: Posto Trevão BR-116 · digite o nome"
                data-testid="fuel-station-name"
              />
              <datalist id="stations-datalist">
                {stations.map((s) => (
                  <option key={s.id} value={s.name}>{s.city ? `${s.city}${s.state ? "/" + s.state : ""}` : ""}</option>
                ))}
              </datalist>
              <p className="text-xs text-slate-500 mt-1">💡 Postos usados antes aparecem no autocomplete. Novo posto é cadastrado automaticamente.</p>
            </div>
            <div><Label>Data</Label><Input type="date" value={form.date} onChange={set("date")} /></div>
            <div><Label>UF</Label>
              <Select value={form.state} onValueChange={set("state")}>
                <SelectTrigger data-testid="fuel-state"><SelectValue placeholder="UF" /></SelectTrigger>
                <SelectContent>{BR_STATES.map(([uf]) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Cidade</Label>
              <CityAutocomplete value={form.city} uf={form.state} onChange={set("city")} placeholder="Digite a cidade" data-testid="fuel-city" />
            </div>
            <div><Label>Litros</Label><Input type="text" inputMode="decimal" placeholder="ex: 250,50" value={form.liters} onChange={set("liters", true)} data-testid="fuel-liters" /></div>
            <div><Label>Valor unitário</Label><Input type="text" inputMode="decimal" placeholder="ex: 6,29" value={form.unit_price} onChange={set("unit_price", true)} data-testid="fuel-unit-price" /></div>
            <div><Label>Total (auto)</Label><Input readOnly value={fmt(form.total)} className="bg-blue-50 font-bold" data-testid="fuel-total" /></div>
            {form.fuel_type === "diesel" && (
              <div className="col-span-2"><Label>KM do painel no abastecimento</Label>
                <Input type="text" inputMode="decimal" placeholder="ex: 125800" value={form.km_at_fueling} onChange={set("km_at_fueling", true)} data-testid="fuel-km-at-fueling" />
                <p className="text-xs text-slate-500 mt-1">🚛 Informe o KM que aparece no painel para calcular km rodados e média km/L automático.</p>
              </div>
            )}
            <div><Label>Forma de pagamento</Label>
              <Select value={form.payment_method} onValueChange={set("payment_method")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="carta_frete">Carta Frete</SelectItem><SelectItem value="nota">Assinou Nota</SelectItem><SelectItem value="pix">PIX (empresa)</SelectItem><SelectItem value="deposito">Depósito (empresa)</SelectItem><SelectItem value="dinheiro">Dinheiro</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Pago por</Label>
              <Select value={form.paid_by} onValueChange={set("paid_by")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="empresa">Empresa</SelectItem><SelectItem value="motorista">Motorista</SelectItem></SelectContent>
              </Select>
            </div>
            <DialogFooter className="col-span-2"><Button type="submit" data-testid="btn-save-fuel" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white">Salvar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Registrar preço (sem viagem, só anotação) */}
      <Dialog open={priceOpen} onOpenChange={setPriceOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Tag size={20} className="text-emerald-600" /> Registrar preço de {priceForm.fuel_type === "diesel" ? "Diesel" : "ARLA"}</DialogTitle></DialogHeader>
          <form onSubmit={submitPrice} className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nome do posto *</Label>
              <Input list="stations-datalist" value={priceForm.station_name} onChange={(e) => setPriceForm({ ...priceForm, station_name: e.target.value })} placeholder="Ex: Posto Ale BR-153" data-testid="price-station" required />
            </div>
            <div><Label>Combustível</Label>
              <Select value={priceForm.fuel_type} onValueChange={(v) => setPriceForm({ ...priceForm, fuel_type: v })}>
                <SelectTrigger data-testid="price-fuel-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="diesel">Diesel</SelectItem><SelectItem value="arla">ARLA</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Preço por litro *</Label>
              <Input type="text" inputMode="decimal" value={priceForm.price} onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })} placeholder="Ex: 5,89" data-testid="price-value" required />
            </div>
            <div><Label>UF</Label>
              <Select value={priceForm.state} onValueChange={(v) => setPriceForm({ ...priceForm, state: v })}>
                <SelectTrigger data-testid="price-state"><SelectValue placeholder="UF" /></SelectTrigger>
                <SelectContent>{BR_STATES.map(([uf]) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Cidade</Label>
              <CityAutocomplete value={priceForm.city} uf={priceForm.state} onChange={(v) => setPriceForm({ ...priceForm, city: v })} placeholder="Digite a cidade" data-testid="price-city" />
            </div>
            <div className="col-span-2"><Label>Data</Label>
              <Input type="date" value={priceForm.date} onChange={(e) => setPriceForm({ ...priceForm, date: e.target.value })} data-testid="price-date" />
            </div>
            <DialogFooter className="col-span-2">
              <Button type="submit" data-testid="btn-save-price" className="bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-1"><Star weight="fill" size={16} /> Salvar preço</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PasswordConfirmDialog
        open={!!pwAction}
        onOpenChange={(v) => !v && setPwAction(null)}
        action={pwAction?.kind === "delete" ? "exclusão" : "edição"}
        description={pwAction?.kind === "delete"
          ? "Este abastecimento será removido permanentemente. Confirme sua senha para continuar."
          : "Você está prestes a editar um abastecimento salvo. Confirme sua senha para aplicar as mudanças."}
        variant={pwAction?.kind === "delete" ? "destructive" : "default"}
        onConfirm={runPwAction}
      />

      <AuditLogCard entity="fueling" title="Histórico de alterações em Abastecimentos" />
    </div>
  );
}
